import React, { useEffect, useState, memo } from 'react';
import { checkAndGrantTier, getAmbassadorStats } from './AmbassadorService';

/**
 * AmbassadorToastService - Tier Milestone Notifications
 * 
 * Listens for ambassador stat updates and shows celebratory toasts when
 * referral count crosses tier thresholds: 5 (Bronze), 25 (Silver), 100 (Gold).
 * 
 * Each tier triggers a single-shot toast that's tracked via localStorage to
 * prevent duplicate notifications across page reloads.
 */

interface Tier {
  threshold: number;
  name: string;
  icon: string;
  color: string;
  bgColor: string;
  message: string;
}

const TIERS: Tier[] = [
  {
    threshold: 5,
    name: 'Bronze Ambassador',
    icon: '🥉',
    color: '#CD7F32',
    bgColor: '#FFF4E6',
    message: 'Your share link is paying off!',
  },
  {
    threshold: 25,
    name: 'Silver Ambassador',
    icon: '🥈',
    color: '#9CA3AF',
    bgColor: '#F3F4F6',
    message: 'You\'re a social legend!',
  },
  {
    threshold: 100,
    name: 'Gold Ambassador',
    icon: '🥇',
    color: '#FFD700',
    bgColor: '#FEF3C7',
    message: 'Elite tier unlocked — exclusive Gold cosmetic available!',
  },
];

const STORAGE_KEY = 'ambassador_tier_seen';

function getSeenTiers(): number[] {
  if (typeof window === 'undefined') return [];
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function markTierSeen(threshold: number) {
  if (typeof window === 'undefined') return;
  const seen = getSeenTiers();
  if (!seen.includes(threshold)) {
    seen.push(threshold);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
  }
}

export const AmbassadorToastService: React.FC = memo(() => {
  const [activeTier, setActiveTier] = useState<Tier | null>(null);

  // Poll ambassador stats every 5 seconds for tier milestones
  useEffect(() => {
    const check = () => {
      const stats = getAmbassadorStats();
      const seen = getSeenTiers();
      
      // Auto-grant cosmetic if a new tier was just crossed
      checkAndGrantTier(stats.totalReferrals);
      
      // Find the highest unseen tier the user has reached
      for (let i = TIERS.length - 1; i >= 0; i--) {
        const tier = TIERS[i];
        if (stats.totalReferrals >= tier.threshold && !seen.includes(tier.threshold)) {
          setActiveTier(tier);
          markTierSeen(tier.threshold);
          
          // Track via GA4
          if (typeof window !== 'undefined' && (window as any).gtag) {
            (window as any).gtag('event', 'ambassador_tier_reached', {
              'tier_name': tier.name,
              'tier_threshold': tier.threshold,
              'total_referrals': stats.totalReferrals,
              'timestamp': new Date().toISOString(),
            });
          }
          break;
        }
      }
    };
    
    check(); // Initial check on mount
    const interval = setInterval(check, 5000);
    return () => clearInterval(interval);
  }, []);

  // Auto-dismiss after 6 seconds
  useEffect(() => {
    if (!activeTier) return;
    const timer = setTimeout(() => setActiveTier(null), 6000);
    return () => clearTimeout(timer);
  }, [activeTier]);

  if (!activeTier) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: '80px',
        right: '16px',
        zIndex: 100,
        background: activeTier.bgColor,
        border: `2px solid ${activeTier.color}`,
        padding: '16px 20px',
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        fontFamily: 'system-ui, -apple-system, Roboto, sans-serif',
        maxWidth: '340px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        animation: 'slideInRight 0.3s cubic-bezier(0.23, 1, 0.32, 1)',
      }}
    >
      <div style={{ fontSize: '32px', lineHeight: 1 }}>{activeTier.icon}</div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: '11px',
            fontWeight: '600',
            color: activeTier.color,
            marginBottom: '2px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
          }}
        >
          Ambassador Status
        </div>
        <div
          style={{
            fontSize: '15px',
            fontWeight: '700',
            color: '#202124',
            marginBottom: '4px',
          }}
        >
          {activeTier.name} Reached!
        </div>
        <div style={{ fontSize: '12px', color: '#5F6368', lineHeight: 1.4 }}>
          {activeTier.message}
        </div>
      </div>
      <button
        onClick={() => setActiveTier(null)}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#5F6368',
          fontSize: '18px',
          cursor: 'pointer',
          padding: '0 4px',
          lineHeight: 1,
        }}
        aria-label="Dismiss notification"
      >
        ✕
      </button>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(120%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
});

AmbassadorToastService.displayName = 'AmbassadorToastService';

/**
 * UnlockAnimationOverlay: Listens for 'ambassador-tier-granted' DOM events and
 * displays a full-screen shimmer animation over the T-Rex sprite when a new
 * cosmetic is auto-granted at a tier milestone.
 */
export const UnlockAnimationOverlay: React.FC = memo(() => {
  const [unlock, setUnlock] = useState<{ cosmeticName: string; cosmeticId: string } | null>(null);

  useEffect(() => {
    const onGrant = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.cosmeticId && detail?.cosmeticName) {
        setUnlock({ cosmeticName: detail.cosmeticName, cosmeticId: detail.cosmeticId });
      }
    };
    window.addEventListener('ambassador-tier-granted', onGrant);
    return () => window.removeEventListener('ambassador-tier-granted', onGrant);
  }, []);

  useEffect(() => {
    if (!unlock) return;
    const timer = setTimeout(() => setUnlock(null), 3500);
    return () => clearTimeout(timer);
  }, [unlock]);

  if (!unlock) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at center, rgba(255,107,53,0.15), transparent 60%)',
        animation: 'fadeShimmer 3.5s ease-out forwards',
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #FF6B35, #FFD700)',
          color: '#fff',
          padding: '24px 36px',
          borderRadius: '12px',
          fontFamily: 'system-ui, -apple-system, Roboto, sans-serif',
          fontWeight: 700,
          textAlign: 'center',
          boxShadow: '0 12px 48px rgba(255,107,53,0.6)',
          animation: 'unlockPop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        <div style={{ fontSize: '11px', letterSpacing: '2px', opacity: 0.85, marginBottom: '8px' }}>
          COSMETIC UNLOCKED
        </div>
        <div style={{ fontSize: '28px', marginBottom: '4px' }}>✨ {unlock.cosmeticName}</div>
        <div style={{ fontSize: '12px', opacity: 0.9 }}>Applied to your T-Rex automatically</div>
      </div>
      <style>{`
        @keyframes fadeShimmer {
          0% { opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes unlockPop {
          0% { transform: scale(0.7); opacity: 0; }
          60% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
});
UnlockAnimationOverlay.displayName = 'UnlockAnimationOverlay';
